import React, { useState } from 'react';
import { Box, Stack, Tooltip, Avatar, Typography, Select, MenuItem } from '@mui/material';
import { alpha } from '@mui/material/styles';

const SignOffStatus = ({ workpaperDetails }) => {
  const [selectedPhase, setSelectedPhase] = useState('DESIGN');

  const getInitials = (name) => {
    if (!name) return '';
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase();
  };

  const isPhaseSignedOff = (phase) => {
    return workpaperDetails?.[0]?.[`${phase}_TESTER_SIGNOFF`] && 
           workpaperDetails?.[0]?.[`${phase}_REVIEWER_SIGNOFF`];
  };

  const getPhaseStatus = (phase) => {
    const testerSignOff = workpaperDetails?.[0]?.[`${phase}_TESTER_SIGNOFF`];
    const reviewerSignOff = workpaperDetails?.[0]?.[`${phase}_REVIEWER_SIGNOFF`];

    if (testerSignOff && reviewerSignOff) return 'completed';
    if (testerSignOff) return 'awaiting_review';
    if (workpaperDetails?.[0]?.[`${phase}_TESTER`]) return 'in_progress';
    return 'not_started';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'awaiting_review':
      case 'in_progress':
        return '#1976d2'; // badge blue color
      default:
        return '#757575'; // medium grey for not started
    }
  };

  const getTooltipContent = (phase) => {
    const tester = workpaperDetails?.[0]?.[`${phase}_TESTER`];
    const reviewer = workpaperDetails?.[0]?.[`${phase}_REVIEWER`];
    const testerDate = workpaperDetails?.[0]?.[`${phase}_TESTER_SIGNOFF`];
    const reviewerDate = workpaperDetails?.[0]?.[`${phase}_REVIEWER_SIGNOFF`];

    return (
      <Box sx={{ p: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          {phase.charAt(0).toUpperCase() + phase.slice(1).toLowerCase()} Phase
        </Typography>
        <Stack spacing={0.5}>
          <Typography variant="body2">
            Tester: {tester || 'Not Assigned'}
            {testerDate && ` (${new Date(testerDate).toLocaleDateString()})`}
          </Typography>
          <Typography variant="body2">
            Reviewer: {reviewer || 'Not Assigned'}
            {reviewerDate && ` (${new Date(reviewerDate).toLocaleDateString()})`}
          </Typography>
        </Stack>
      </Box>
    );
  };

  const phases = ['DESIGN', 'INTERIM', 'RF', 'FINAL'];

  const status = getPhaseStatus(selectedPhase);
  const statusColor = getStatusColor(status);
  const tester = workpaperDetails?.[0]?.[`${selectedPhase}_TESTER`];
  const reviewer = workpaperDetails?.[0]?.[`${selectedPhase}_REVIEWER`];
  const testerSignOff = workpaperDetails?.[0]?.[`${selectedPhase}_TESTER_SIGNOFF`];
  const reviewerSignOff = workpaperDetails?.[0]?.[`${selectedPhase}_REVIEWER_SIGNOFF`];

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Select
        value={selectedPhase}
        onChange={(e) => setSelectedPhase(e.target.value)}
        size="small"
        variant="outlined"
        sx={{
          minWidth: 90,
          height: 32,
          fontSize: '0.813rem',
          backgroundColor: 'transparent',
          '& .MuiOutlinedInput-notchedOutline': {
            border: '1px solid #E0E0E0'
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            border: '1px solid #BDBDBD'
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: '1px solid #9E9E9E'
          },
          '& .MuiSelect-select': {
            paddingTop: '4px',
            paddingBottom: '4px'
          }
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              border: '1px solid #E0E0E0',
              borderRadius: '4px',
              mt: 0.5
            }
          }
        }}
      >
        {phases.map((phase) => (
          <MenuItem 
            key={phase} 
            value={phase}
            sx={{
              fontSize: '0.813rem',
              minHeight: '32px',
              padding: '4px 12px',
              '&.Mui-selected': {
                backgroundColor: '#F5F5F5',
                '&:hover': {
                  backgroundColor: '#EEEEEE'
                }
              },
              '&:hover': {
                backgroundColor: '#FAFAFA'
              }
            }}
          >
            {phase}
          </MenuItem>
        ))}
      </Select>
      <Tooltip title={getTooltipContent(selectedPhase)} arrow>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {tester && (
            <Avatar
              sx={{
                width: 24,
                height: 24,
                fontSize: '0.75rem',
                bgcolor: testerSignOff ? alpha(statusColor, 0.9) : alpha('#9E9E9E', 0.3),
                border: '1px solid',
                borderColor: testerSignOff ? statusColor : '#9E9E9E',
                color: testerSignOff ? '#fff' : '#757575'
              }}
            >
              {getInitials(tester)}
            </Avatar>
          )}
          {reviewer && (
            <Avatar
              sx={{
                width: 24,
                height: 24,
                fontSize: '0.75rem',
                bgcolor: reviewerSignOff ? alpha(statusColor, 0.9) : alpha('#9E9E9E', 0.3),
                border: '1px solid',
                borderColor: reviewerSignOff ? statusColor : '#9E9E9E',
                color: reviewerSignOff ? '#fff' : '#757575'
              }}
            >
              {getInitials(reviewer)}
            </Avatar>
          )}
        </Stack>
      </Tooltip>
    </Stack>
  );
};

export default SignOffStatus;
